/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import { Location } from '@angular/common';
import { Injectable, InjectionToken } from '@angular/core';
import { FeedbackCommentDetails, PostCommentRequest } from '@app/app/page-modules/course/models/course-assignments.model';
import {
  AnnotationTarget,
  AnnotationsWithDraft,
  AssignmentTaskFormAnnotationTarget,
  PdfAnnotation,
  SaveAnnotationRequest,
} from '@app/app/page-modules/pdf-annotation-editor/models/pdf-annotation.model';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { environment } from '@app/environments/environment';
import { uuidV4 } from '@embedpdf/models';
import { isEqual } from 'lodash-es';
import { Observable, map, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface PdfAnnotationDataService {
  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]>;
  loadAnnotations(target: AnnotationTarget, includeDraft: boolean): Observable<AnnotationsWithDraft>;
  commitDraft(target: AnnotationTarget): Observable<void>;
  deleteAnnotations(target: AnnotationTarget, deleteDraft?: boolean): Observable<void>;
  /** A reply on an assignment PDF is a feedback comment about its mark. Resolves to the comment's id. */
  postAnnotationComment(target: AssignmentTaskFormAnnotationTarget, annotationId: string, body: string): Observable<string>;
  editAnnotationComment(target: AssignmentTaskFormAnnotationTarget, commentId: string, body: string): Observable<void>;
  deleteAnnotationComment(target: AssignmentTaskFormAnnotationTarget, commentId: string): Observable<void>;
}

export const PDF_ANNOTATION_DATA_SERVICE = new InjectionToken<PdfAnnotationDataService>('PdfAnnotationDataService');

@Injectable()
export class RealPdfAnnotationDataService implements PdfAnnotationDataService {
  constructor(private restClient: RestClientService) {}

  deleteAnnotations(target: AnnotationTarget): Observable<void> {
    return of(void 0);
  }

  loadAnnotations(target: AnnotationTarget, includeDraft: boolean): Observable<AnnotationsWithDraft> {
    const { url, params } = this.buildAnnotationsRequest(target);
    return this.restClient.get<AnnotationsWithDraft>(url, { ...params, includeDraft }).pipe(map(({ body }) => body));
  }

  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]> {
    const { url, params } = this.buildAnnotationsRequest(request.target);
    const { engineVersion } = request;
    return this.restClient
      .post(url, request.annotations, engineVersion ? { ...params, engineVersion } : params)
      .pipe(map(() => request.annotations));
  }

  commitDraft(target: AnnotationTarget): Observable<void> {
    // Published server-side when the marker sends their feedback.
    if (target.type === 'assignmentTaskForm') {
      return of(void 0);
    }
    return this.restClient.post<void>(`${this.buildAnnotationsRequest(target).url}/commit`).pipe(map(() => void 0));
  }

  postAnnotationComment(target: AssignmentTaskFormAnnotationTarget, annotationId: string, body: string): Observable<string> {
    const request: PostCommentRequest = {
      body,
      kind: 'DISCUSSION',
      taskUid: target.taskUid,
      formUid: target.formUid,
      file: target.file,
      annotationId,
    };
    return this.restClient.post<FeedbackCommentDetails>(this.commentsUrl(target), request).pipe(map(({ body: comment }) => comment.id));
  }

  editAnnotationComment(target: AssignmentTaskFormAnnotationTarget, commentId: string, body: string): Observable<void> {
    return this.restClient.put(Location.joinWithSlash(this.commentsUrl(target), commentId), { body }).pipe(map(() => void 0));
  }

  deleteAnnotationComment(target: AssignmentTaskFormAnnotationTarget, commentId: string): Observable<void> {
    return this.restClient.delete(Location.joinWithSlash(this.commentsUrl(target), commentId)).pipe(map(() => void 0));
  }

  private commentsUrl({ courseId, assignmentId, userUid }: AssignmentTaskFormAnnotationTarget): string {
    return [
      environment.localServiceOverrides?.resourcesBaseUrl || environment.apiRootUrl || '',
      '/courses',
      courseId,
      'assignments',
      assignmentId,
      'learners',
      userUid,
      'comments',
    ].reduce(Location.joinWithSlash);
  }

  private buildAnnotationsRequest(target: AnnotationTarget): { url: string; params: Record<string, string> } {
    switch (target.type) {
      case 'learnerForm':
        return {
          url: Location.joinWithSlash(
            environment.apiRootUrl || '',
            `/resources/document-annotations/cards/${target.cardUid}/forms/${target.formUid}/users/${target.userUid}/files/${encodeURIComponent(target.file)}`,
          ),
          params: {},
        };
      case 'assignmentTaskForm':
        return {
          url: [
            environment.localServiceOverrides?.resourcesBaseUrl || environment.apiRootUrl || '',
            '/courses',
            target.courseId,
            'assignments',
            target.assignmentId,
            'tasks',
            target.taskUid,
            'forms',
            target.formUid,
            'users',
            target.userUid,
            'annotations',
          ].reduce(Location.joinWithSlash),
          params: target.cycle == null ? { file: target.file } : { file: target.file, cycle: String(target.cycle) },
        };
      default: {
        const unknownTarget: never = target;
        throw new Error(`Unknown annotation target: ${JSON.stringify(unknownTarget)}`);
      }
    }
  }
}

@Injectable()
export class MockPdfAnnotationDataService implements PdfAnnotationDataService {
  private readonly STORAGE_KEY = 'pdf_annotations';

  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const existingIndex = annotations.findIndex((a) => isEqual(a.target, request.target));

        const now = new Date();
        const annotation: PdfAnnotation = {
          target: request.target,
          ...(request.isDraft
            ? { draftAnnotations: request.annotations, annotations: annotations[existingIndex]?.annotations ?? undefined }
            : { annotations: request.annotations }),
        };

        if (existingIndex >= 0) {
          annotations[existingIndex] = annotation;
        } else {
          annotations.push(annotation);
        }

        this.saveToStorage(annotations);
        observer.next(annotation.annotations);
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  loadAnnotations(target: AnnotationTarget): Observable<AnnotationsWithDraft> {
    return new Observable<AnnotationsWithDraft>((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const found = annotations.find((a) => isEqual(a.target, target));
        observer.next({ committed: found?.annotations, draft: found?.draftAnnotations });
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    }).pipe(delay(2000));
  }

  commitDraft(target: AnnotationTarget): Observable<void> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const existingIndex = annotations.findIndex((a) => isEqual(a.target, target));
        if (existingIndex >= 0 && annotations[existingIndex].draftAnnotations) {
          console.log(annotations[existingIndex]);
          annotations[existingIndex] = { ...annotations[existingIndex], annotations: annotations[existingIndex].draftAnnotations };
          console.log(annotations[existingIndex]);
          this.saveToStorage(annotations);
        }
        observer.next();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  deleteAnnotations(target: AnnotationTarget): Observable<void> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const filtered = annotations.filter((a) => isEqual(a.target, target));
        this.saveToStorage(filtered);
        observer.next();
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  postAnnotationComment(): Observable<string> {
    return of(uuidV4());
  }

  editAnnotationComment(): Observable<void> {
    return of(void 0);
  }

  deleteAnnotationComment(): Observable<void> {
    return of(void 0);
  }

  private getAllFromStorage(): PdfAnnotation[] {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) {
      return [];
    }
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  }

  private saveToStorage(annotations: PdfAnnotation[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(annotations));
  }
}
